/**
 * QMS Ticket Form
 * Comprehensive form for creating and editing QMS tickets and CAPAs
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSession } from '@/hooks/useSession';
import { useCreateQmsTicket, useUpdateQmsTicket } from '../../hooks/useQmsTicket';
import { useDeleteQmsTicket } from '../../hooks/useQmsBoard';
import { useQmsPermissions } from '../../hooks/useQmsPermissions';
import { useUploadQmsAttachment } from '../../hooks/useQmsAttachments';
import { QmsRichTextEditor } from '../shared/QmsRichTextEditor';
import { QmsPendingAttachments } from '../shared/QmsPendingAttachments';
import {
  QmsDepartmentSelect,
  QmsPrioritySelect,
  QmsSeveritySelect,
  QmsSubtypeSelect,
  QmsStatusSelect,
  QmsChangeTypeSelect,
} from '../shared/QmsFormFields';
import { QmsAccountSelect } from '../shared/QmsAccountSelect';
import { QmsUserSelect } from '../shared/QmsUserSelect';
import { QmsAssignmentPromptDialog } from '../shared/QmsAssignmentPromptDialog';
import { QmsApprovalPanel } from '../workflow/QmsApprovalPanel';
import { QmsCeoReviewPanel } from '../workflow/QmsCeoReviewPanel';
import { QmsIntakePanel } from '../workflow/QmsIntakePanel';
import { QmsChangeControlPanel } from '../workflow/QmsChangeControlPanel';
import { QmsCcbMembersEditor } from '../workflow/QmsCcbMembersEditor';
import { QmsQualityActionItemsEditor } from '../workflow/QmsQualityActionItemsEditor';
import { QmsEffectivenessReviewPanel } from '../workflow/QmsEffectivenessReviewPanel';
import { QmsQualitySignaturesPanel } from '../workflow/QmsQualitySignaturesPanel';
import { QmsCreateCapaModal } from './QmsCreateCapaModal';
import { QmsAiInvestigationSection } from './sections/QmsAiInvestigationSection';
import { QmsLineItemsSection } from './sections/QmsLineItemsSection';
import { QmsGatewaysSection } from './sections/QmsGatewaysSection';
import { QmsShippingSection } from './sections/QmsShippingSection';
import { QmsSiteVisitsSection } from './sections/QmsSiteVisitsSection';
import { Save, Trash2, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { getQmsOrganisationId } from '../../utils/organisation';
import { formatQmsTags, parseQmsTags } from '../../utils/tags';
import type {
  QmsPendingFile,
  QmsRcaMethod,
  QmsChangeType,
  QmsTicket,
  QmsTicketPayload,
  QmsTicketStatus,
} from '../../types/qms.types';
import { extractId } from '../../types/qms.types';
import { isProtectedQualityEvent } from '../../utils/workflow';

interface QmsTicketFormProps {
  ticket: QmsTicket;
  onSave: () => void;
  onClose: () => void;
  onDelete?: () => void;
  // Pending files props (for new tickets)
  pendingFiles?: QmsPendingFile[];
  onAddFiles?: (files: FileList | File[]) => void;
  onRemoveFile?: (id: string) => void;
  onClearFiles?: () => void;
  onUpdateFileStatus?: (id: string, status: QmsPendingFile['status'], error?: string) => void;
}

export function QmsTicketForm({
  ticket,
  onSave,
  onClose,
  onDelete,
  pendingFiles,
  onAddFiles,
  onRemoveFile,
  onClearFiles,
  onUpdateFileStatus,
}: QmsTicketFormProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const {
    canDeleteTickets,
    canAssignTickets,
    canChangeTicketStatus,
    canApproveTicketStatus,
    canVerifyQuality,
  } = useQmsPermissions();
  const createMutation = useCreateQmsTicket();
  const updateMutation = useUpdateQmsTicket();
  const deleteMutation = useDeleteQmsTicket();
  const uploadAttachment = useUploadQmsAttachment();

  const isNew = !ticket._id;
  const isCAPA = ticket.type === 'capa';
  const isClient = ticket.type === 'client';
  const { data: teams = [] } = useTeams();
  const qmsOrganisation = getQmsOrganisationId(user?.organisation);
  const hasPendingFiles = pendingFiles && pendingFiles.length > 0;
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<QmsTicket>>({
    ...ticket,
    priority: ticket.priority || 3,
    severity_level: ticket.severity_level ?? 1,
    status: ticket.status || 'submitted', // New tickets start in submitted status
    organisation: qmsOrganisation,
  });
  const [tagsInput, setTagsInput] = useState(formatQmsTags(ticket.tags));

  // Sync form data when ticket is updated externally (e.g., AI findings applied)
  // This preserves any unsaved user edits while updating server-changed fields
  const [lastSyncedAt, setLastSyncedAt] = useState(ticket.updated_at);
  if (ticket.updated_at !== lastSyncedAt) {
    // Ticket was updated externally - sync the form data
    setLastSyncedAt(ticket.updated_at);
    setFormData((prev) => ({
      ...prev,
      // Only sync fields that are typically updated by external actions (AI, approvals)
      risk_level: ticket.risk_level,
      risk_assessment: ticket.risk_assessment,
      investigation_findings: ticket.investigation_findings,
      root_cause_analysis: ticket.root_cause_analysis,
      rca_method: ticket.rca_method,
      corrective_measures: ticket.corrective_measures,
      quality_action_items: ticket.quality_action_items,
      effectiveness_review_due_at: ticket.effectiveness_review_due_at,
      effectiveness_review_status: ticket.effectiveness_review_status,
      effectiveness_review_metrics: ticket.effectiveness_review_metrics,
      change_approval_status: ticket.change_approval_status,
      change_approval_records: ticket.change_approval_records,
      tags: ticket.tags,
      priority: ticket.priority,
      qd_approval_status: ticket.qd_approval_status,
      ceo_review_status: ticket.ceo_review_status,
      severity_level: ticket.severity_level,
      intake_decision: ticket.intake_decision,
    }));
    setTagsInput(formatQmsTags(ticket.tags));
  }

  // Collapsible sections
  const [investigationExpanded, setInvestigationExpanded] = useState(true);
  const [capaExpanded, setCapaExpanded] = useState(isCAPA);
  const [changeControlExpanded, setChangeControlExpanded] = useState(false);

  // Error state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  // Create CAPA modal state
  const [showCreateCapaModal, setShowCreateCapaModal] = useState(false);

  // Assignment prompt state (for status change to "in_progress")
  const [showAssignmentPrompt, setShowAssignmentPrompt] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<QmsTicketStatus | null>(null);

  // Can create CAPA only for existing tickets (not CAPAs)
  const canCreateCapa = !isNew && !isCAPA;

  /**
   * Handle status change from dropdown
   * Shows assignment prompt if changing to 'in_progress' while unassigned
   */
  const handleStatusChange = (newStatus: QmsTicketStatus) => {
    if (!canChangeTicketStatus) {
      toast.error(t('errors.noStatusPermission'));
      return;
    }
    if (newStatus === 'approved' && newStatus !== ticket.status && !canApproveTicketStatus) {
      toast.error(t('errors.noApproveStatusPermission'));
      return;
    }
    if (!isNew && isProtectedQualityEvent({
      type: ticket.type,
      change_control_required: formData.change_control_required,
    }) && newStatus !== ticket.status) {
      toast.error('CAPA and change-control state changes require an electronic signature from the Kanban board.');
      return;
    }

    const isUnassigned = !extractId(formData.assigned_to);
    const isStatusChanging = newStatus !== formData.status;

    // Only prompt if actually changing TO in_progress (not just selecting same status)
    if (newStatus === 'in_progress' && isStatusChanging && isUnassigned && canAssignTickets) {
      // Store the pending status and show the prompt
      setPendingStatus(newStatus);
      setShowAssignmentPrompt(true);
      // Don't update status yet - wait for prompt response
      return;
    }

    // No prompt needed - update status directly
    setFormData((prev) => ({ ...prev, status: newStatus }));
  };

  /**
   * Handle assignment from the prompt dialog
   */
  const handleAssignmentPromptAssign = (userId: string) => {
    // Use functional update to avoid stale closure
    setFormData((prev) => ({
      ...prev,
      assigned_to: userId,
      status: pendingStatus || 'in_progress',
    }));
    setShowAssignmentPrompt(false);
    setPendingStatus(null);
  };

  /**
   * Handle skip from the prompt dialog
   */
  const handleAssignmentPromptSkip = () => {
    // Use functional update to avoid stale closure
    setFormData((prev) => ({
      ...prev,
      status: pendingStatus || 'in_progress',
    }));
    setShowAssignmentPrompt(false);
    setPendingStatus(null);
  };

  /**
   * Handle dialog close (cancel)
   */
  const handleAssignmentPromptOpenChange = (open: boolean) => {
    if (!open) {
      // Dialog was closed without action - don't change status
      setShowAssignmentPrompt(false);
      setPendingStatus(null);
    }
  };

  const handleTagsInputChange = (value: string) => {
    setTagsInput(value);
    setFormData((prev) => ({ ...prev, tags: parseQmsTags(value) }));
  };

  // Validation
  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = t('validation.titleRequired');
    }

    if (!qmsOrganisation) {
      newErrors.organisation = t('validation.organisationRequired');
    }

    if (formData.severity_level === 0 && !formData.intake_decision) {
      newErrors.severity_level = t('validation.level0RequiresIntake');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Extract user-friendly error message from API error
  const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    return t('errors.unexpectedError');
  };

  // Upload pending files after ticket creation
  const uploadPendingFiles = async (ticketId: string): Promise<{ success: number; failed: number }> => {
    if (!hasPendingFiles || !pendingFiles || !onUpdateFileStatus) {
      return { success: 0, failed: 0 };
    }

    setIsUploadingFiles(true);
    let successCount = 0;
    let failedCount = 0;

    for (const pendingFile of pendingFiles) {
      onUpdateFileStatus(pendingFile.id, 'uploading');
      try {
        await uploadAttachment.mutateAsync({ ticketId, file: pendingFile.file });
        onUpdateFileStatus(pendingFile.id, 'success');
        successCount++;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        onUpdateFileStatus(pendingFile.id, 'error', errorMessage);
        failedCount++;
      }
    }

    setIsUploadingFiles(false);
    return { success: successCount, failed: failedCount };
  };

  // Show toast based on upload results
  const showUploadToast = (success: number, failed: number) => {
    if (success > 0 && failed === 0) {
      toast.success(t('attachments.pending.uploadSuccess', { count: success }));
    } else if (failed > 0 && success > 0) {
      toast.warning(t('attachments.pending.uploadPartial', { success, failed }));
    } else if (failed > 0 && success === 0) {
      toast.error(t('attachments.pending.uploadFailed'));
    }
  };

  // Sanitize payload to ensure populated fields are converted to IDs
  const sanitizePayload = (data: Partial<QmsTicket>): QmsTicketPayload => {
    const sanitized = { ...data };

    // Convert populated user fields to IDs using extractId helper
    sanitized.assigned_to = extractId(sanitized.assigned_to);
    sanitized.created_by = extractId(sanitized.created_by);
    sanitized.qd_approved_by = extractId(sanitized.qd_approved_by);
    sanitized.ceo_reviewed_by = extractId(sanitized.ceo_reviewed_by);

    // Convert populated related_tickets ticket field to IDs
    if (sanitized.related_tickets && Array.isArray(sanitized.related_tickets)) {
      sanitized.related_tickets = sanitized.related_tickets.map((link) => ({
        ...link,
        ticket: extractId(link.ticket) || link.ticket,
      }));
    }

    return sanitized as QmsTicketPayload;
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setApiError(null); // Clear previous errors

    const payload = sanitizePayload({
      ...formData,
      tags: parseQmsTags(tagsInput),
      organisation: qmsOrganisation,
      type: ticket.type,
    });
    if (!isNew && payload.status === ticket.status) {
      delete (payload as Partial<QmsTicketPayload>).status;
    }
    if (!isNew && payload.change_approval_status === ticket.change_approval_status) {
      delete (payload as Partial<QmsTicketPayload>).change_approval_status;
    }
    if (
      !isNew
      && JSON.stringify(payload.quality_action_items || [])
        === JSON.stringify(ticket.quality_action_items || [])
    ) {
      delete (payload as Partial<QmsTicketPayload>).quality_action_items;
    }

    try {
      if (isNew) {
        const createdTicket = await createMutation.mutateAsync(payload);

        // Upload pending files if any
        if (hasPendingFiles) {
          const { success, failed } = await uploadPendingFiles(createdTicket._id);
          showUploadToast(success, failed);
        }

        toast.success(t('messages.ticketCreated'));
        onSave();
        onClose();
      } else {
        await updateMutation.mutateAsync({
          ticketId: ticket._id!,
          payload,
        });

        toast.success(t('messages.ticketSaved'));
        onSave();
      }
    } catch (error) {
      console.error('Failed to save ticket:', error);
      setApiError(getErrorMessage(error));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!ticket._id) return;
    if (!confirm(t('actions.confirmDelete'))) return;

    setApiError(null); // Clear previous errors

    try {
      await deleteMutation.mutateAsync(ticket._id);
      onDelete?.();
      onClose();
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      setApiError(getErrorMessage(error));
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending || isUploadingFiles;
  const isDeleting = deleteMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* API Error Display */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">{t('errors.title')}</h3>
              <p className="text-sm text-red-700 mt-1">{apiError}</p>
            </div>
            <button
              type="button"
              onClick={() => setApiError(null)}
              className="flex-shrink-0 text-red-600 hover:text-red-800"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('form.sections.basicInfo')}</h3>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('form.fields.title')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title || ''}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('form.placeholders.title')}
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('form.fields.description')}
          </label>
          <QmsRichTextEditor
            value={formData.description || ''}
            onChange={(html) => setFormData({ ...formData, description: html })}
            placeholder={t('form.placeholders.description')}
          />
        </div>

        {/* Common form fields: Priority, Status, Assigned To */}
        {isClient ? (
          <>
            {/* Client ticket editable fields */}
            <div className="grid grid-cols-3 gap-4">
              {/* Status */}
              {!isNew && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.fields.status')}
                  </label>
                  <QmsStatusSelect
                    value={formData.status || 'client_submitted'}
                    onChange={handleStatusChange}
                    disabled={!canChangeTicketStatus}
                    canApproveStatus={canApproveTicketStatus}
                  />
                </div>
              )}

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.priority')}
                </label>
                <QmsPrioritySelect
                  value={formData.priority || 3}
                  onChange={(value) => setFormData({ ...formData, priority: value })}
                />
              </div>

              {/* Assigned To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.assignedTo')}
                </label>
                <QmsUserSelect
                  value={formData.assigned_to}
                  onChange={(user) => setFormData({ ...formData, assigned_to: user })}
                  disabled={!canAssignTickets}
                  placeholder={t('form.placeholders.selectAssignee')}
                />
              </div>
            </div>

            {/* Client-specific: Important/Urgent/Due Date */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.important', 'Important')}
                </label>
                <select
                  value={formData.important ?? ''}
                  onChange={(e) => setFormData({ ...formData, important: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                >
                  <option value="">-</option>
                  {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.urgent', 'Urgent')}
                </label>
                <select
                  value={formData.urgent ?? ''}
                  onChange={(e) => setFormData({ ...formData, urgent: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                >
                  <option value="">-</option>
                  {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.dueDate')}
                </label>
                <input
                  type="date"
                  value={formData.due_date ? new Date(formData.due_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
            </div>

            {/* Client-specific: Financial fields */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.quoteNumber', 'Quote #')}
                </label>
                <input
                  type="text"
                  value={formData.quote_number || ''}
                  onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.purchaseOrder', 'PO #')}
                </label>
                <input
                  type="text"
                  value={formData.purchase_order || ''}
                  onChange={(e) => setFormData({ ...formData, purchase_order: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.value', 'Value')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.value ?? ''}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
            </div>

            {/* Client-specific: Contact fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.contactName', 'Contact Name')}
                </label>
                <input
                  type="text"
                  value={formData.user_name || ''}
                  onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.contactEmail', 'Contact Email')}
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
            </div>

            {/* Tags and Account */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.tags')}
                </label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => handleTagsInputChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                  placeholder={t('form.placeholders.tags')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.account')}
                </label>
                <QmsAccountSelect
                  value={formData.account_id}
                  onChange={(accountId) => setFormData({ ...formData, account_id: accountId })}
                />
              </div>
            </div>

            {/* Teams (visibility scope) — shares ticket with selected EOS teams */}
            <div className="grid gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Teams
                <span className="ml-2 text-xs font-normal text-gray-500">
                  Pick the teams that should see this ticket. Leave empty for admin-only.
                </span>
              </label>
              {teams.length === 0 ? (
                <p className="text-sm text-muted-foreground">No teams available.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-3">
                  {teams.map((team) => {
                    const checked = (formData.team_ids ?? []).includes(team.id);
                    return (
                      <label
                        key={team.id}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(c) => {
                            const next = new Set(formData.team_ids ?? []);
                            if (c) next.add(team.id);
                            else next.delete(team.id);
                            setFormData({ ...formData, team_ids: Array.from(next) });
                          }}
                        />
                        <span>{team.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Internal Notes / Solution */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.internalNotes', 'Internal Notes')}
                </label>
                <textarea
                  value={formData.internal_notes || ''}
                  onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.solution', 'Solution')}
                </label>
                <textarea
                  value={formData.solution || ''}
                  onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* QMS-specific form fields */}
            {/* Priority, Status, Department Row */}
            <div className="grid grid-cols-3 gap-4">
              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.priority')}
                </label>
                <QmsPrioritySelect
                  value={formData.priority || 3}
                  onChange={(value) => setFormData({ ...formData, priority: value })}
                />
              </div>

              {/* Status */}
              {!isNew && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.fields.status')}
                  </label>
                  <QmsStatusSelect
                    value={formData.status || 'submitted'}
                    onChange={handleStatusChange}
                    disabled={!canChangeTicketStatus}
                    canApproveStatus={canApproveTicketStatus}
                  />
                </div>
              )}

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.department')}
                </label>
                <QmsDepartmentSelect
                  value={formData.department || ''}
                  onChange={(value) => setFormData({ ...formData, department: value || undefined })}
                />
              </div>
            </div>

            {/* Subtype, Severity Level, Due Date Row */}
            <div className={`grid gap-4 ${isCAPA ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {/* Subtype */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.subtype')}
                </label>
                <QmsSubtypeSelect
                  value={formData.subtype || ''}
                  onChange={(value) => setFormData({ ...formData, subtype: value || undefined })}
                />
              </div>

              {/* Severity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.severityLevel')}
                </label>
                <QmsSeveritySelect
                  value={formData.severity_level ?? 1}
                  onChange={(value) => setFormData({ ...formData, severity_level: value })}
                  error={!!errors.severity_level}
                />
                {errors.severity_level && (
                  <p className="mt-1 text-sm text-red-600">{errors.severity_level}</p>
                )}
              </div>

              {/* Due Date - hidden for CAPAs (they have per-action due dates below) */}
              {!isCAPA && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.fields.dueDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.due_date ? new Date(formData.due_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                  />
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.fields.tags')}
              </label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => handleTagsInputChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                placeholder={t('form.placeholders.tags')}
              />
            </div>

            {/* Account and Assigned To Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.account')}
                </label>
                <QmsAccountSelect
                  value={formData.account_id}
                  onChange={(accountId) => setFormData({ ...formData, account_id: accountId })}
                />
              </div>

              {/* Assigned To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.assignedTo')}
                </label>
                <QmsUserSelect
                  value={formData.assigned_to}
                  onChange={(user) => setFormData({ ...formData, assigned_to: user })}
                  disabled={!canAssignTickets}
                  placeholder={t('form.placeholders.selectAssignee')}
                />
                {!canAssignTickets && (
                  <p className="mt-1 text-xs text-gray-500">{t('form.hints.noAssignPermission')}</p>
                )}
              </div>
            </div>

            {/* Pending Attachments (only for new tickets) */}
            {isNew && onAddFiles && onRemoveFile && onClearFiles && (
              <div className="border-t pt-4 mt-4">
                <QmsPendingAttachments
                  pendingFiles={pendingFiles || []}
                  onAddFiles={onAddFiles}
                  onRemoveFile={onRemoveFile}
                  onClearAll={onClearFiles}
                  disabled={isSaving}
                  isUploading={isUploadingFiles}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Investigation Section - hidden for CAPAs and client tickets */}
      {!isCAPA && !isClient && (
        <div className="border-t pt-6">
          <button
            type="button"
            onClick={() => setInvestigationExpanded(!investigationExpanded)}
            className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-atek-blue"
          >
            {investigationExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
            {t('form.sections.investigationAndImmediateActions')}
          </button>

          {investigationExpanded && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.rootCause')}
                </label>
                <QmsRichTextEditor
                  value={formData.root_cause_analysis || ''}
                  onChange={(html) =>
                    setFormData({ ...formData, root_cause_analysis: html })
                  }
                  placeholder={t('form.placeholders.rootCause')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.investigationFindings')}
                </label>
                <QmsRichTextEditor
                  value={formData.investigation_findings || ''}
                  onChange={(html) =>
                    setFormData({ ...formData, investigation_findings: html })
                  }
                  placeholder={t('form.placeholders.investigationFindings')}
                />
              </div>

              {/* Immediate Action */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.immediateAction')}
                </label>
                <QmsRichTextEditor
                  value={formData.immediate_actions || ''}
                  onChange={(html) =>
                    setFormData({ ...formData, immediate_actions: html })
                  }
                  placeholder={t('form.placeholders.immediateAction')}
                />
                <input
                  type="date"
                  value={
                    formData.due_date_immediate
                      ? new Date(formData.due_date_immediate).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, due_date_immediate: e.target.value })
                  }
                  className="mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                  placeholder={t('form.placeholders.dueDate')}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Investigation Section (only for existing non-CAPA, non-client tickets when AI investigation is enabled) */}
      {!isNew && !isCAPA && !isClient && user?.ai_investigation_enabled && (
        <QmsAiInvestigationSection
          ticketId={ticket._id}
          defaultExpanded={true}
          hasAppliedFindings={!!ticket.ai_summary}
        />
      )}

      {/* CAPA Actions Section (only for CAPAs) */}
      {isCAPA && (
        <div className="border-t pt-6">
          <button
            type="button"
            onClick={() => setCapaExpanded(!capaExpanded)}
            className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-atek-blue"
          >
            {capaExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
            {t('form.sections.capaActions')}
          </button>

          {capaExpanded && (
            <div className="mt-4 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Root cause analysis
                </label>
                <QmsRichTextEditor
                  value={formData.root_cause_analysis || ''}
                  onChange={(html) =>
                    setFormData({ ...formData, root_cause_analysis: html })
                  }
                  placeholder="Document the causal chain and supporting evidence…"
                />
              </div>

              <div>
                <label htmlFor="qms-rca-method" className="block text-sm font-medium text-gray-700 mb-1">
                  RCA method
                </label>
                <select
                  id="qms-rca-method"
                  value={formData.rca_method || ''}
                  onChange={(event) => setFormData({
                    ...formData,
                    rca_method: event.target.value as QmsRcaMethod,
                  })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a method</option>
                  <option value="five_whys">5 Whys</option>
                  <option value="ishikawa">Ishikawa / Fishbone</option>
                  <option value="fault_tree">Fault Tree Analysis</option>
                  <option value="other">Other documented method</option>
                </select>
              </div>

              <QmsQualityActionItemsEditor
                mode="capa"
                items={formData.quality_action_items || []}
                onChange={(qualityActionItems) => setFormData({
                  ...formData,
                  quality_action_items: qualityActionItems,
                })}
                canVerify={canVerifyQuality}
              />
            </div>
          )}
        </div>
      )}

      {/* Change Control Section - hidden for client tickets */}
      {!isClient && <div className="border-t pt-6">
        <button
          type="button"
          onClick={() => setChangeControlExpanded(!changeControlExpanded)}
          className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-atek-blue"
        >
          {changeControlExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          {t('form.sections.changeControl')}
        </button>

        {changeControlExpanded && (
          <div className="mt-4 space-y-4">
            {/* Change Control Required */}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.change_control_required || false}
                onChange={(e) =>
                  setFormData({ ...formData, change_control_required: e.target.checked })
                }
                disabled={!isNew && ticket.change_control_required}
                className="rounded border-gray-300 text-atek-blue focus:ring-atek-blue disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-700">{t('form.fields.changeControlRequired')}</span>
            </label>
            {!isNew && ticket.change_control_required && (
              <p className="text-xs text-slate-500">
                Change control stays enabled for the life of this record. Editing its approved
                scope, risk, CCB, SOP link, or task plan requires CCB re-approval.
              </p>
            )}

            {/* Additional fields shown when change control is required */}
            {formData.change_control_required && (
              <>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current state</label>
                    <textarea
                      value={formData.change_current_state || ''}
                      onChange={(event) => setFormData({ ...formData, change_current_state: event.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Describe the validated state today…"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Proposed state</label>
                    <textarea
                      value={formData.change_proposed_state || ''}
                      onChange={(event) => setFormData({ ...formData, change_proposed_state: event.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Describe the intended controlled state…"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
                    <textarea
                      value={formData.change_justification || ''}
                      onChange={(event) => setFormData({ ...formData, change_justification: event.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Explain why this change is necessary…"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.fields.changeType')}
                  </label>
                  <QmsChangeTypeSelect
                    value={formData.change_type || ''}
                    onChange={(value) =>
                      setFormData({ ...formData, change_type: value as QmsChangeType })
                    }
                  />
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">Mandatory 3×3 risk matrix</h4>
                      <p className="text-xs text-slate-500">Risk score = Severity × Probability</p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2 text-center shadow-sm">
                      <div className="text-xs uppercase tracking-wide text-slate-500">Score</div>
                      <div className="text-xl font-semibold text-slate-900">
                        {formData.risk_severity && formData.risk_probability
                          ? formData.risk_severity * formData.risk_probability
                          : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor="qms-risk-severity" className="mb-1 block text-sm font-medium text-slate-700">
                        Severity
                      </label>
                      <select
                        id="qms-risk-severity"
                        value={formData.risk_severity || ''}
                        onChange={(event) => setFormData({
                          ...formData,
                          risk_severity: event.target.value ? Number(event.target.value) as 1 | 2 | 3 : undefined,
                        })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">Select severity</option>
                        <option value="1">1 — Minor</option>
                        <option value="2">2 — Major</option>
                        <option value="3">3 — Critical</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="qms-risk-probability" className="mb-1 block text-sm font-medium text-slate-700">
                        Probability
                      </label>
                      <select
                        id="qms-risk-probability"
                        value={formData.risk_probability || ''}
                        onChange={(event) => setFormData({
                          ...formData,
                          risk_probability: event.target.value ? Number(event.target.value) as 1 | 2 | 3 : undefined,
                        })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">Select probability</option>
                        <option value="1">1 — Unlikely</option>
                        <option value="2">2 — Possible</option>
                        <option value="3">3 — Likely</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.fields.changeImpactAssessment')}
                  </label>
                  <QmsRichTextEditor
                    value={formData.change_impact_assessment || ''}
                    onChange={(html) =>
                      setFormData({ ...formData, change_impact_assessment: html })
                    }
                    placeholder={t('form.placeholders.changeImpactAssessment')}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Change Control Board
                  </label>
                  <QmsCcbMembersEditor
                    memberIds={formData.change_approver_ids || []}
                    onChange={(changeApproverIds) => setFormData({
                      ...formData,
                      change_approver_ids: changeApproverIds,
                    })}
                  />
                </div>

                <QmsQualityActionItemsEditor
                  mode="change_control"
                  items={formData.quality_action_items || []}
                  onChange={(qualityActionItems) => setFormData({
                    ...formData,
                    quality_action_items: qualityActionItems,
                  })}
                  canVerify={canVerifyQuality}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.fields.implementationOutcome')}
                  </label>
                  <QmsRichTextEditor
                    value={formData.implementation_outcome || ''}
                    onChange={(html) =>
                      setFormData({ ...formData, implementation_outcome: html })
                    }
                    placeholder={t('form.placeholders.implementationOutcome')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    QA post-verification review
                  </label>
                  <QmsRichTextEditor
                    value={formData.implementation_review_notes || ''}
                    onChange={(html) =>
                      setFormData({ ...formData, implementation_review_notes: html })
                    }
                    placeholder="Record QA review of objective testing evidence and documentation updates…"
                  />
                </div>

                <div className="rounded-lg border border-slate-200 p-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.requires_sop_update || false}
                      onChange={(event) => setFormData({
                        ...formData,
                        requires_sop_update: event.target.checked,
                      })}
                      className="rounded border-gray-300 text-atek-blue focus:ring-atek-blue"
                    />
                    <span className="text-sm text-gray-700">This change requires a controlled SOP update</span>
                  </label>
                  {formData.requires_sop_update && (
                    <div className="mt-3">
                      <label htmlFor="qms-linked-document" className="mb-1 block text-sm font-medium text-gray-700">
                        Controlled Document ID
                      </label>
                      <input
                        id="qms-linked-document"
                        value={formData.linked_document_id || ''}
                        onChange={(event) => setFormData({
                          ...formData,
                          linked_document_id: event.target.value.trim() || undefined,
                        })}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Closure is blocked until this eQMS document is Effective.
                      </p>
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.training_applied || false}
                    onChange={(e) =>
                      setFormData({ ...formData, training_applied: e.target.checked })
                    }
                    className="rounded border-gray-300 text-atek-blue focus:ring-atek-blue"
                  />
                  <span className="text-sm text-gray-700">{t('form.fields.trainingApplied')}</span>
                </label>
              </>
            )}
          </div>
        )}
      </div>}

      {/* Client Ticket Sub-Resource Sections */}
      {isClient && !isNew && ticket._id && (
        <div className="space-y-2">
          <QmsLineItemsSection ticketId={ticket._id} lineItems={ticket.line_items || []} />
          <QmsGatewaysSection ticketId={ticket._id} gateways={ticket.gateways || []} />
          <QmsShippingSection ticketId={ticket._id} shipping={ticket.shipping || []} />
          <QmsSiteVisitsSection ticketId={ticket._id} siteVisits={ticket.site_visits || []} />
        </div>
      )}

      {/* L0 Intake Decision Panel (only for existing L0 tickets, not client) */}
      {!isNew && !isClient && ticket.severity_level === 0 && (
        <QmsIntakePanel ticket={ticket} onDecisionComplete={onSave} />
      )}

      {/* QD Approval Panel (only for existing tickets, not client) */}
      {!isNew && !isClient && (
        <QmsApprovalPanel ticket={ticket} onApprovalComplete={onSave} />
      )}

      {/* CEO Review Panel (only for existing tickets that require CEO review, not client) */}
      {!isNew && !isClient && ticket.requires_ceo_review && (
        <QmsCeoReviewPanel ticket={ticket} onReviewComplete={onSave} />
      )}

      {!isNew && isCAPA && (
        <QmsEffectivenessReviewPanel ticket={ticket} onCompleted={onSave} />
      )}

      {!isNew && isProtectedQualityEvent(ticket) && (
        <QmsQualitySignaturesPanel signatures={ticket.quality_signatures || []} />
      )}

      {/* Change Control Panel (only for existing tickets with change control required, not client) */}
      {!isNew && !isClient && ticket.change_control_required && (
        <QmsChangeControlPanel ticket={ticket} onApprovalComplete={onSave} />
      )}

      {/* Form Actions */}
      <div className="border-t pt-6 flex items-center justify-between sticky bottom-0 bg-white pb-6">
        <div className="flex items-center gap-2">
          {!isNew && onDelete && canDeleteTickets && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {isDeleting ? t('actions.deleting') : t('actions.delete')}
            </button>
          )}

          {/* Create CAPA button */}
          {canCreateCapa && !isClient && (
            <button
              type="button"
              onClick={() => setShowCreateCapaModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('actions.createCapa')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('actions.cancel')}
          </button>

          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2 bg-atek-blue text-white rounded-lg hover:bg-atek-blue-dark transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {isSaving ? t('actions.saving') : isNew ? t('actions.create') : t('actions.save')}
          </button>
        </div>
      </div>

      {/* Create CAPA Modal */}
      {canCreateCapa && (
        <QmsCreateCapaModal
          ticket={ticket}
          isOpen={showCreateCapaModal}
          onClose={() => setShowCreateCapaModal(false)}
          onCapaCreated={() => {
            // Show success toast - board will auto-refresh via query invalidation
            toast.success(t('createCapa.success'));
          }}
        />
      )}

      {/* Assignment Prompt Dialog (shown when changing status to "In Progress" while unassigned) */}
      <QmsAssignmentPromptDialog
        open={showAssignmentPrompt}
        onOpenChange={handleAssignmentPromptOpenChange}
        onAssign={handleAssignmentPromptAssign}
        onSkip={handleAssignmentPromptSkip}
      />
    </form>
  );
}
